import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Avatar from '@material-ui/core/Avatar';
import Typography from '@material-ui/core/Typography';
import LinearProgress from '@material-ui/core/LinearProgress';
import PersonIcon from '@material-ui/icons/Person';
import GroupIcon from '@material-ui/icons/Group';
import { makeStyles, withStyles, fade } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import { ReactComponent as VideocamIcon } from 'assets/icons/video.svg';
import Alert from '@material-ui/lab/Alert';
import moment from 'moment';
import get from 'lodash/get';
import { Chip } from '@material-ui/core';
import PRODUCTS from 'constant/product-types';
import { toUserTimezone } from 'utils';
import UpgradeModal from 'modules/parent/purchase/Upgrade';
import times from 'lodash/times';
import { IsMeetingEnabled } from 'utils/verify-meeting-access';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight:theme.spacing(60)
  },
  cardContent: {
    padding: theme.spacing(3),
  },
  header: {
    'display': 'flex',
    'alignItems': 'center',
    'marginBottom': theme.spacing(2),
    '& h6': {
      fontSize: 18,
      fontWeight: 600,
    },
  },
  avatar: {
    width: 46,
    height: 46,
    border: '4px solid #E9ECF1',
    marginRight: theme.spacing(2),
  },
  label: {
    textTransform: 'uppercase',
  },
  videocam: {
    '& path': {
    fill: theme.palette.primary.main
    }
  },
  group: {
    '& path': {
      fill: theme.palette.secondary.main
      }
  },
  alert: {
    alignItems:'center'
  }
}));

const ProgressBar = withStyles((theme) => ({
  root: {
    display: 'flex',
    marginRight: -2,
    marginLeft: -2,
  },
  step: {
    backgroundColor: fade(theme.palette.text.secondary, 0.3),
    height: 8,
    flex: 1,
    marginRight: 2,
    marginLeft: 2,
  },
  done: {
    backgroundColor: theme.palette.success.main,
  },
}))((props) => {
  const { total, done, classes } = props;
  return (
    <Box className={classes.root}>
      {times(total, (i) => (
        <Box
          key={i}
          className={cx(classes.step, { [classes.done]: i <= done })}
        ></Box>
      ))}
    </Box>
  );
});

const PRODUCTS_LOOKUP = PRODUCTS.reduce((memo, i) => {
  memo[i.value] = i.label;
  return memo;
}, {});

const StudentCard = ({ student, confLink = true, role}) => {
  const classes = useStyles();
  const [showUpgrade, toggleUpgrade] = useState();
  const nextDue = get(student, 'colleges.0.app_deadline');
  const isMeetingEnable = IsMeetingEnabled(student.user_id);

  const onCall = (e) => {
    e.stopPropagation();
  };
  const onUpGrade = (e) => {
    e.preventDefault();
    toggleUpgrade(true);
  };

  const subEndDays = student.subscriptions.length
    ? toUserTimezone(student.subscriptions[0].end_date).diff(new Date(), 'd')
    : null;

  return (
    <>
      <Link to={`/students/${student.user_id}/timeline`}>
        <Card className={classes.root}>
          <CardContent className={classes.cardContent}>
            <div className={classes.header}>
              <Avatar className={classes.avatar} src={student.user_pic}>
                <PersonIcon />
              </Avatar>
              <Box flex={1}>
                <Typography variant="subtitle1">
                  {student.user_first_name} {student.user_last_name}
                  {student?.product_type && (
                    <Chip
                      style={{ marginLeft: 8 }}
                      label={PRODUCTS_LOOKUP[student?.product_type]}
                    />
                  )}
                </Typography>
                {student.group_student && (
                  <Box display="flex" alignItems="center">
                    <GroupIcon fontSize="small" color="disabled" />
                    &nbsp;
                    <Typography variant="body2" color="textSecondary">
                      {student.group_student.group.name}
                    </Typography>
                  </Box>
                )}
              </Box>
              {confLink && isMeetingEnable && (
                <a
                  rel="noopener noreferrer"
                  onClick={onCall}
                  target="_blank"
                  href={
                    student.group_student
                      ? `/meeting/group/${student.group_student.group_id}`
                      : `/meeting/${student.user_cognito_sub}`
                  }
                >
                  <IconButton
                    color={student.group_student ? 'secondary' : 'primary'}
                  >
                    <VideocamIcon className={cx(classes.videocam,student.group_student && classes.group)} />
                  </IconButton>
                </a>
              )}
            </div>
            {student.is_trial && student.trial_end_date && (
              <Box marginBottom={2}>
                <Alert
                  className={classes.alert}
                  severity="info"
                  action={
                    role === 'parent' && (
                      <Button
                        component="a"
                        onClick={onUpGrade}
                        variant="contained"
                        color="primary"
                      >
                        Upgrade Now
                      </Button>
                    )
                  }
                >
                  <Typography variant="smallBold">
                    Trial ends on{' '}
                    {toUserTimezone(student.trial_end_date).format('L')}
                  </Typography>
                </Alert>
              </Box>
            )}
            {student.is_trial && !student.trial_end_date && (
              <Box marginBottom={2}>
                <Alert className={classes.alert} severity="info">
                  <Typography variant="smallBold">Trial</Typography>
                </Alert>
              </Box>
            )}
            {subEndDays !== null && subEndDays <= 30 && subEndDays > 1 && (
              <Box marginBottom={2}>
                <Alert className={classes.alert} severity="warning">
                  <Typography color="secondary" variant="smallBold">
                    Your program ends in {subEndDays} days.
                  </Typography>
                </Alert>
              </Box>
            )}
            {!student.is_trial && subEndDays <= 0 && (
              <Box marginBottom={2}>
                <Alert className={classes.alert} severity="error">
                  <Typography color="secondary" variant="smallBold">
                    Your program has ended.
                  </Typography>
                </Alert>
              </Box>
            )}
            <Box marginTop={4} marginBottom={4}>
              <Box display='flex' justifyContent='space-between'>
              <Typography gutterBottom variant="body2">
                {get(student, 'milestones.0.name', 'Milestone Progress')}
              </Typography>
              <Typography gutterBottom variant="body2">
                {get(student, 'milestones.0.order', null) &&
                  `${get(student, 'milestones.0.order')} of ${get(
                    student,
                    'milestones_aggregate.aggregate.count',
                  )}`}
              </Typography>
              </Box>
              <ProgressBar
                done={student.exercises_summary?.done}
                total={student.exercises_summary?.total}
              />
            </Box>
            <Divider />
            <Box marginTop={6}>
              <Grid container spacing={4}>
                <Grid item sm={6} md={6} lg={6}>
                  <Typography
                    variant="smallBold"
                    color="textSecondary"
                    className={classes.label}
                  >
                    Days until next app due
                  </Typography>
                  <Typography variant="h2">
                    {nextDue
                      ? moment
                          .utc(nextDue, 'YYYY-MM-DD')
                          .diff(moment.utc(), 'd')
                      : '-'}
                  </Typography>
                </Grid>
                <Grid item sm={6} md={6} lg={6}>
                  <Typography
                    variant="smallBold"
                    color="textSecondary"
                    className={classes.label}
                  >
                    {'Applications submitted'}
                  </Typography>
                  <Typography variant="h2">
                    {student.app_submitted?.aggregate?.count || 0}
                  </Typography>
                </Grid>
                <Grid item sm={6} md={6} lg={6}>
                  <Typography
                    variant="smallBold"
                    color="textSecondary"
                    className={classes.label}
                  >
                    Colleges in school list
                  </Typography>
                  <Typography variant="h2">
                    {student.colleges_aggregate.aggregate.count}
                  </Typography>
                </Grid>
                <Grid item sm={6} md={6} lg={6}>
                  <Typography
                    variant="smallBold"
                    color="textSecondary"
                    className={classes.label}
                  >
                    Essays written
                  </Typography>
                  <Typography variant="h2">
                    {student.documents_aggregate.aggregate.count}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Link>
      {showUpgrade && (
        <UpgradeModal student={student} onClose={() => toggleUpgrade(false)} />
      )}
    </>
  );
};

export default StudentCard;
